import React, { useState, useEffect, useRef } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { GoogleLogin } from '@react-oauth/google'
import { useAuth } from '../contexts/AuthContext'
import { useBranding } from '../contexts/BrandingContext'
import { useToast } from '../contexts/ToastContext'
import { posthog } from '../main.jsx'
import { resolveLearnerHome } from '../lib/navigation/resolveLearnerHome' // #1521
import { toUserFacingMessage } from '../utils/userFacingError.js'
import './LoginModal.css'

function LoginModal({ isOpen, onClose, onSuccess }) {
  const navigate = useNavigate()
  const location = useLocation()
  // Capture the URL email/signin params on first open so re-opens (which keep ?signin
  // around if the consumer never strips it) don't re-fire One Tap or re-pre-fill email.
  const initialEmailRef = useRef(null)
  const consumedSigninHintRef = useRef(false)
  const { t } = useTranslation()
  const _branding = useBranding()
  const { showToast } = useToast()
  const [email, setEmail] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [message, setMessage] = useState('')
  const [error, setError] = useState('')
  const [googleEmphasised, setGoogleEmphasised] = useState(false)
  const inputRef = useRef(null)
  const modalRef = useRef(null)

  const { requestMagicLink, loginWithGoogle, sessionExpiredMessage, clearSessionExpiredMessage } = useAuth()

  // Pre-fill email from URL ?email= and read ?signin=google hint (Issue #1155)
  const urlParams = new URLSearchParams(location.search)
  const urlEmail = urlParams.get('email')
  const urlSigninHint = urlParams.get('signin')

  // Focus input when modal opens — but only when Google sign-in isn't configured.
  // Issue #1155 Phase 2: with Google promoted to the primary CTA, auto-focusing
  // the email field directly contradicts the new hierarchy (h10 human-test catch
  // 2026-05-06).
  useEffect(() => {
    if (!isOpen || !inputRef.current) return
    if (import.meta.env.VITE_GOOGLE_CLIENT_ID) return
    setTimeout(() => inputRef.current?.focus(), 100)
  }, [isOpen])

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose()
      }
    }
    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [isOpen, onClose])

  // Clear messages when modal closes
  useEffect(() => {
    if (!isOpen) {
      setMessage('')
      setError('')
      setEmail('')
    }
  }, [isOpen])

  // Pre-fill email + activate Google emphasis when ?signin=google is present (#1155).
  // We strip ?signin from the URL after consumption so re-opens don't re-fire One Tap
  // (P1 from code-review).
  useEffect(() => {
    if (!isOpen) return

    // Pre-fill email only the first time we see one for this open cycle
    if (urlEmail && initialEmailRef.current !== urlEmail) {
      initialEmailRef.current = urlEmail
      setEmail(decodeURIComponent(urlEmail))
    }

    if (
      urlSigninHint === 'google'
      && !consumedSigninHintRef.current
      && import.meta.env.VITE_GOOGLE_CLIENT_ID
    ) {
      consumedSigninHintRef.current = true
      setGoogleEmphasised(true)
      posthog?.capture?.('auth_deep_link_triggered', {
        provider: 'google',
        surface: 'login_modal',
        has_email_hint: !!urlEmail,
      })

      // Strip ?signin so a subsequent close+reopen won't re-trigger One Tap
      const cleanParams = new URLSearchParams(location.search)
      cleanParams.delete('signin')
      const newSearch = cleanParams.toString()
      navigate(
        { pathname: location.pathname, search: newSearch ? `?${newSearch}` : '' },
        { replace: true },
      )
    }
    // intentional one-time setup on open
  }, [isOpen]) // eslint-disable-line react-hooks/exhaustive-deps

  // Clear session expired message after showing
  useEffect(() => {
    if (sessionExpiredMessage && isOpen) {
      const timer = setTimeout(() => {
        clearSessionExpiredMessage()
      }, 10000)
      return () => clearTimeout(timer)
    }
  }, [sessionExpiredMessage, isOpen, clearSessionExpiredMessage])

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!email.trim() || isLoading) return

    setIsLoading(true)
    setMessage('')
    setError('')
    posthog?.capture?.('auth_provider_selected', { provider: 'magic_link', surface: 'login_modal' })

    try {
      const response = await requestMagicLink(email)
      setMessage(response.message)
      setEmail('')
      if (onSuccess) onSuccess()
    } catch (err) {
      setError(toUserFacingMessage(err, t, 'Failed to send magic link'))
    } finally {
      setIsLoading(false)
    }
  }

  const handleGoogleSuccess = async (credentialResponse) => {
    setIsLoading(true)
    setError('')
    setMessage('')
    posthog?.capture?.('auth_provider_selected', { provider: 'google', surface: 'login_modal' })
    try {
      const response = await loginWithGoogle(credentialResponse.credential)
      if (response.success) {
        const expectedEmail = urlEmail ? decodeURIComponent(urlEmail).toLowerCase() : null
        const actualEmail = response.user?.email?.toLowerCase()
        if (expectedEmail && actualEmail && expectedEmail !== actualEmail) {
          showToast(
            t('auth.googleMismatchToast', {
              googleEmail: response.user.email,
              inviteEmail: decodeURIComponent(urlEmail),
            }),
            'info',
            10000,
          )
        }
        onClose()
        // #1521: resolved from the login RESPONSE branding — context values
        // are stale inside this closure.
        navigate(resolveLearnerHome(response.user?.branding))
      }
    } catch (err) {
      setError(toUserFacingMessage(err, t, 'Failed to sign in with Google'))
    } finally {
      setIsLoading(false)
    }
  }

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose()
    }
  }

  if (!isOpen) return null

  const googleEnabled = !!import.meta.env.VITE_GOOGLE_CLIENT_ID

  return (
    <div
      className="login-modal-backdrop"
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="login-modal-title"
    >
      <div className="login-modal" ref={modalRef}>
        <button
          className="login-modal-close"
          onClick={onClose}
          aria-label="Close"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M18 6L6 18M6 6l12 12" />
          </svg>
        </button>

        <div className="login-modal-header">
          <h2 id="login-modal-title">{t('login.welcome')}</h2>
          <p>{t('login.subtitle')}</p>
        </div>

        {sessionExpiredMessage && (
          <div className="login-modal-notice session-expired">
            <span className="notice-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                <path d="M7 11V7a5 5 0 0110 0v4" />
              </svg>
            </span>
            <span>{sessionExpiredMessage}</span>
          </div>
        )}

        {/* Google Sign-In - primary CTA when configured (#1155) */}
        {googleEnabled && (
          <div className={`login-modal-google${googleEmphasised ? ' emphasised' : ''}`}>
            <GoogleLogin
              onSuccess={handleGoogleSuccess}
              onError={() => {
                setError(t('login.googleSignInFailed'))
              }}
              theme="outline"
              size="large"
              text="signin_with"
              width="100%"
              useOneTap={googleEmphasised}
              login_hint={urlEmail ? decodeURIComponent(urlEmail) : undefined}
            />
          </div>
        )}

        {googleEnabled && (
          <div className="login-modal-divider">
            <span>{t('login.orSignInWithEmail')}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="login-modal-form">
          <div className="login-modal-field">
            <label htmlFor="login-email">{t('login.emailLabel')}</label>
            <input
              ref={inputRef}
              type="email"
              id="login-email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t('login.emailPlaceholder')}
              required
              disabled={isLoading}
              autoComplete="email"
            />
          </div>

          <button
            type="submit"
            className="login-modal-submit"
            disabled={isLoading || !email.trim()}
          >
            {isLoading ? (
              <span className="login-modal-loading">
                <span className="spinner"></span>
                {t('login.sending')}
              </span>
            ) : (
              t('login.sendMagicLink')
            )}
          </button>
        </form>

        {message && (
          <div className="login-modal-notice success">
            <span className="notice-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M22 11.08V12a10 10 0 11-5.93-9.14" />
                <polyline points="22 4 12 14.01 9 11.01" />
              </svg>
            </span>
            <div>
              <strong>{t('login.checkEmail')}</strong>
              <p>{message}</p>
            </div>
          </div>
        )}

        {error && (
          <div className="login-modal-notice error">
            <span className="notice-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
            </span>
            <span>{error}</span>
          </div>
        )}

        <div className="login-modal-footer">
          <p>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
            </svg>
            {t('login.noPassword')}
          </p>
        </div>
      </div>
    </div>
  )
}

export default LoginModal
